import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import jwt_decode from 'jwt-decode';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { LoginModel } from 'src/app/models/identification/login.model';
import { AutorisationAggregate } from '../../aggregats/autorisation/autorisation.aggregate';
import { FonctionLogicielle } from '../../constantes/fonctions-logicielle-constantes';
import { OperationFonction } from '../../constantes/operations-fonctions.constantes';
import { JwtInterceptorService } from '../../interceptors/jwt-interceptor.service';
import { TokenModel } from '../../models/identification/token.model';
import { DroitModel } from "../../models/profils/droit.model";
import { InformationsProfilModel } from '../../models/profils/informations-profil.model';
import { ToastInfoModel } from '../../models/toasts/toast-info.model';
import { UtilisateurCourantModel } from '../../models/utilisateurs/utilisateur-courant.model';
import { ConfigService } from '../config/config.service';
import { NotificationToastService } from '../notifications/notification-toast.service';
import { LocalStorageTokenService } from './local-storage-token.service';

@Injectable({
  providedIn: 'root',
})
export class UtilisateurCourantService {
  readonly utilisateurCourantEvent: BehaviorSubject<UtilisateurCourantModel> = new BehaviorSubject<
    UtilisateurCourantModel
  >(null);
  private baseUrlApi: string;
  private requestGetInformationsProfilsModelSharedObservable: Observable<InformationsProfilModel>;
  constructor(
    private client: HttpClient,
    private jwtInterceptor: JwtInterceptorService,
    private localStorage: LocalStorageTokenService,
    private notificationToastService: NotificationToastService,
    private configService: ConfigService
  ) {
    this.baseUrlApi = this.configService.config.urlApi;
    this.requestGetInformationsProfilsModelSharedObservable = new BehaviorSubject<InformationsProfilModel>(null);
    this.initSharedObservables();

  }

  private static TokenToUserModel(token: TokenModel): UtilisateurCourantModel {
    if (!token) {
      return null;
    }

    const data: {jti: number; client: number; user_guid: string} = jwt_decode(token.value);
    const user: UtilisateurCourantModel = new UtilisateurCourantModel();
    user.id = data.jti;
    user.idClient = data.client;
    user.guid = data.user_guid;
    return user;
  }

  isLogguedIn(): Observable<boolean> {
    if (!this.isLocallyLogguedIn) {
      return of(false);
    }
    return this.client.get(`${this.baseUrlApi}/api/u/isAuthenticated`).pipe(
      tap(() => this.emitUtilisateurCourantIfChanged()),
      map(() => true),
      catchError(() => {
        this.notificationToastService.push(new ToastInfoModel('Vous avez été déconnecté.'));
        return of(false);
      }),
    );
  }

  logIn(login: LoginModel): Observable<void> {
    return this.client.post(`${this.baseUrlApi}/api/u/identity/login`, login).pipe(
      map((token: TokenModel) => {
        this.localStorage.setCurrentToken(token);
        this.emitUtilisateurCourantIfChanged();
      }),
    );
  }

  getInformationsProfilUtilisateur(): Observable<InformationsProfilModel> {
    return this.requestGetInformationsProfilsModelSharedObservable;
  }

  getGuidUtilisateur(): string {
    return UtilisateurCourantService.TokenToUserModel(this.localStorage.getCurrentToken())?.guid;
  }

  logout() {
    this.resetLocalInformations();
  }

  refreshToken(): Observable<void> {
    return this.client.get(`${this.baseUrlApi}/api/u/identity/refreshToken`).pipe(
      map((token: TokenModel) => {
        this.localStorage.setCurrentToken(token);
        this.emitUtilisateurCourantIfChanged();
      }),
    );
  }

  isAuthorized(autorization: AutorisationAggregate): Observable<boolean> {
    if (!this.isLocallyLogguedIn) {
      return of(false);
    }
    return this.getInformationsProfilUtilisateur().pipe(
      map(informations => {
        if (informations) {
          return autorization.isAuthorizedForDroits(informations.droits);
        } else {
          return false;
        }
      }),
    );
  }

  withAuthorization(
    fonction: FonctionLogicielle,
    operation: OperationFonction,
    ifAllowed: Observable<any>,
    valueIfError: Observable<any> = of(null),
  ) {
    return this.isAuthorized(new AutorisationAggregate(fonction, operation)).pipe(
      switchMap(isAutorized => (isAutorized ? ifAllowed : valueIfError)),
    );
  }

  getDroitsUtilisateur(): Observable<DroitModel[]> {
    if(this.configService.isFusionAuthConfigured()){
      return this.getDroitsUtilisateurForFusionAuth()
    }else{
      return this.getDroitsUtilisateurForLoginPassword();
    }
  }

  public getDroitsUtilisateurForFusionAuth(): Observable<DroitModel[]> {
    let authorizationTokenFusionAuth = this.localStorage.getCurrentAuthorizationToken();
    if (authorizationTokenFusionAuth) {
      const decodedToken: any = jwt_decode(authorizationTokenFusionAuth.value);
      const droits: DroitModel[] = decodedToken.user_rights.droits.map((droit: any) => ({
        codeFonctionLogicielle: droit.codeFonctionLogicielle,
        codesOperations: droit.codesOperations
      }));
      return of(droits);
    }
    return of([]);
  }

  public getDroitsUtilisateurForLoginPassword(): Observable<any>{
    return this.getInformationsProfilUtilisateur().pipe(map(infos => infos.droits))
  }

  private get utilisateurCourant(): UtilisateurCourantModel {
    return UtilisateurCourantService.TokenToUserModel(this.localStorage.getCurrentToken());
  }

  private initSharedObservables(): void {
    if(this.configService.isFusionAuthConfigured()){
      this.initSharedObservablesForFusionAuth();
    }else{
      this.initSharedObservablesForLoginPassword();
    }
  }

  public initSharedObservablesForFusionAuth(): void {
    const authorizationToken = this.localStorage.getCurrentAuthorizationToken();
    if (authorizationToken) {
      const decodedToken: any = jwt_decode(authorizationToken.value);
      const informationsProfil: InformationsProfilModel = {
        codeRole: decodedToken.user_rights.codeRole,
        droits: decodedToken.user_rights.droits,
      };
      this.requestGetInformationsProfilsModelSharedObservable = of(informationsProfil);}
  }

  public initSharedObservablesForLoginPassword(): void {
    this.requestGetInformationsProfilsModelSharedObservable =
    this.client.get<InformationsProfilModel>(`${this.baseUrlApi}/api/u/identity/getInformationsProfilUtilisateur`)
      .pipe(shareReplay(1));


  }

  private emitUtilisateurCourantIfChanged() {
    if (this.utilisateurCourantEvent.value !== this.utilisateurCourant) {
      this.utilisateurCourantEvent.next(this.utilisateurCourant);
    }
  }


  private get isLocallyLogguedIn(): boolean {
    return this.utilisateurCourant != null;
  }

  public sendToken(token: string): Observable<any> {
    return this.client.post(
        `${this.configService.config.urlApi}/api/u/Identity/LoginWithFusionAuth`,
        null,
        { headers: { 'tokenFromFusionAuth': token } }
    ).pipe(
      map((token: any) => {
        this.localStorage.setCurrentToken(token.authenticationToken);
        this.localStorage.setCurrentAuthorizationToken(token.authorizationToken);
        this.emitUtilisateurCourantIfChanged();
        this.getGuidUtilisateur();
        this.initSharedObservables();
      }),
    );
  }

  private resetLocalInformations(): void {
    if(this.configService.isFusionAuthConfigured()){
      this.localStorage.setCurrentAuthorizationToken(null);
    }
    this.localStorage.setCurrentToken(null);
    this.initSharedObservables();
    this.utilisateurCourantEvent.next(null);
  }

  public getUserMode(droits: DroitModel[], fonctionLogicielleName: FonctionLogicielle, operationCode: OperationFonction){
    let droit = droits.find(d => d.codeFonctionLogicielle === fonctionLogicielleName);
    if(!droit) return;
    return droit.codesOperations.some(c => c === operationCode);
  }
}
